
import { supabase } from '@/integrations/supabase/client';
import { debugSupabaseRequest } from './supabase-helpers';

/**
 * Determines if a user has a client profile
 */
export async function hasClientProfile(userId: string): Promise<boolean> {
  try {
    console.log('Checking client profile for user:', userId);
    
    const { data, error } = await supabase
      .from('clients')
      .select('id')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error checking client profile:', error);
      debugSupabaseRequest('clients', 'select', error);
      return false;
    }

    const result = !!data;
    console.log('Client profile check result:', result);
    return result;
  } catch (err) {
    console.error('Unexpected error checking client profile:', err);
    return false;
  }
}

/**
 * Determines if a user has a practitioner profile
 */
export async function hasPractitionerProfile(userId: string): Promise<boolean> {
  try {
    console.log('Checking practitioner profile for user:', userId);
    
    const { data, error } = await supabase
      .from('practitioners')
      .select('id')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error checking practitioner profile:', error);
      debugSupabaseRequest('practitioners', 'select', error);
      return false;
    }

    const result = !!data;
    console.log('Practitioner profile check result:', result);
    return result;
  } catch (err) {
    console.error('Unexpected error checking practitioner profile:', err);
    return false;
  }
}

/**
 * Determines if user is an admin
 */
export async function isAdmin(userId: string): Promise<boolean> {
  try {
    console.log('Checking admin status for user:', userId);
    
    const { data, error } = await supabase
      .from('admins')
      .select('user_id')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error checking admin status:', error);
      debugSupabaseRequest('admins', 'select', error);
      return false;
    }

    const result = !!data;
    console.log('Admin status check result:', result);
    return result;
  } catch (err) {
    console.error('Unexpected error checking admin status:', err);
    return false;
  }
}

/**
 * Determines if user is a client or practitioner or admin or none
 * Used for routing after login
 */
export async function determineUserType(userId: string, preferredRole?: 'client' | 'practitioner' | 'admin'): Promise<'client' | 'practitioner' | 'admin' | 'unknown'> {
  console.log('Determining user type for:', userId, 'preferred role:', preferredRole);
  
  // If user has a preferred role and it's valid, ONLY check for that role
  if (preferredRole) {
    console.log('Checking if user has preferred role:', preferredRole);
    
    let hasRole = false;
    switch (preferredRole) {
      case 'admin':
        hasRole = await isAdmin(userId);
        console.log('Admin check result:', hasRole);
        break;
      case 'practitioner':
        hasRole = await hasPractitionerProfile(userId);
        console.log('Practitioner check result:', hasRole);
        break;
      case 'client':
        hasRole = await hasClientProfile(userId);
        console.log('Client check result:', hasRole);
        break;
    }
    
    if (hasRole) {
      console.log('User verified for preferred role:', preferredRole);
      return preferredRole;
    } else {
      console.log('User does NOT have preferred role:', preferredRole, '- returning unknown');
      return 'unknown';
    }
  }
  
  // Only if no preferred role, use fallback priority order (client > practitioner > admin)
  console.log('No preferred role, checking default priority...');
  
  const isClient = await hasClientProfile(userId);
  if (isClient) {
    console.log('User is client (fallback)');
    return 'client';
  }
  
  const isPractitioner = await hasPractitionerProfile(userId);
  if (isPractitioner) {
    console.log('User is practitioner (fallback)');
    return 'practitioner';
  }
  
  const isAdminUser = await isAdmin(userId);
  if (isAdminUser) {
    console.log('User is admin (fallback)');
    return 'admin';
  }
  
  console.log('User type is unknown - no profile found');
  return 'unknown';
}
